import { partitionByPaper } from "./partitionByPaper";
// submitTagsFlow.js
// Lógica de submit compartilhada entre TagGenerator (desktop) e MobileTagGenerator
// (mobile): monta o payload já particionado por papel e traduz erros de
// partitionByPaper (ex.: INCOMPLETE_SUBGRADES) para o formato usado por
// focusFirstTagErrorField. Mantém os dois pontos de entrada sem duplicar regra.

const isManualCard = (card) => card?.source === "manual";

const buildTagsSendData = (tagsData) =>
  (tagsData ?? [])
    .filter((d) => d?.select)
    .map((d) => {
      const part = partitionByPaper(d);
      return { ...d, invertida: part.invertidaEffective };
    });

const buildPartitionErrorData = (error) => {
  if (error?.errorData?.cards?.length) {
    return error.errorData;
  }
  if (error?.serial) {
    return { cards: [{ serial: error.serial, fields: error.fields || [] }] };
  }
  return null;
};

const modelIdOf = (serial) => {
  const gm = serial?.gradableModel;
  if (!gm) return null;
  if (typeof gm === "string") return gm;
  return gm._id || gm.id || null;
};

const baseTagFromModel = (serial) => {
  const model = serial?.gradableModel;
  const tag = model?.tag;
  return {
    nome: tag?.name ?? model?.name,
    raridade: tag?.rarity ?? model?.rarity,
    edition: tag?.edition ?? model?.edition,
    ano: (tag?.year ?? model?.year)?.toString?.() ?? tag?.year ?? model?.year,
    idioma: tag?.language ?? model?.language,
    num: tag?.numbering ?? model?.numbering,
  };
};

const cardDiffersFromBase = (card, base) =>
  String(card?.nome ?? "") !== String(base?.nome ?? "") ||
  String(card?.raridade ?? "") !== String(base?.raridade ?? "") ||
  String(card?.edition ?? "") !== String(base?.edition ?? "") ||
  String(card?.ano ?? "") !== String(base?.ano ?? "") ||
  String(card?.idioma ?? "") !== String(base?.idioma ?? "") ||
  String(card?.num ?? "") !== String(base?.num ?? "");

/**
 * Escolhe 1 card por GradableModel para o texto da tag — prefere o que diverge
 * do valor atual do modelo (a edição do usuário). Vários seriais do mesmo
 * modelo não podem sobrescrever uns aos outros no backend.
 */
const pickTagWinnerByModel = (selected, serials = []) => {
  const bySerial = Object.fromEntries(
    (serials || []).map((s) => [s?.serialNumber, s])
  );
  const byModel = new Map();

  for (const card of selected) {
    const serial = bySerial[card.serial];
    const key = String(modelIdOf(serial) || card.serial);
    const existing = byModel.get(key);
    if (!existing) {
      byModel.set(key, card);
      continue;
    }
    const base = baseTagFromModel(serial);
    const cardEdited = cardDiffersFromBase(card, base);
    const existingEdited = cardDiffersFromBase(
      existing,
      baseTagFromModel(bySerial[existing.serial])
    );
    if (cardEdited && !existingEdited) {
      byModel.set(key, card);
    }
  }

  return byModel;
};

/**
 * Payload para POST /gradable-models/tags.
 * Um item por serial selecionado: texto da tag vem do vencedor do modelo
 * (dedupe), `invertida` é por serial (já normalizada por partitionByPaper).
 */
const buildTagSavePayload = (tagsData, serials = []) => {
  const selected = buildTagsSendData(tagsData).filter((c) => !isManualCard(c));
  const bySerial = Object.fromEntries(
    (serials || []).map((s) => [s?.serialNumber, s])
  );
  const winners = pickTagWinnerByModel(selected, serials);

  return selected.map((card) => {
    const serial = bySerial[card.serial];
    const key = String(modelIdOf(serial) || card.serial);
    const winner = winners.get(key) || card;
    return {
      serial: card.serial,
      nome: winner.nome,
      raridade: winner.raridade,
      edition: winner.edition,
      ano: winner.ano,
      idioma: winner.idioma,
      num: winner.num,
      invertida: card.invertida === true,
    };
  });
};

const buildManualAuditPayload = (tagsData) =>
  (tagsData ?? []).filter((d) => d?.select && isManualCard(d));

/** Aplica o tag salvo e o flag invertida de volta no array de serials. */
const applySavedTagsToSerials = (serials, savePayload) => {
  const bySerial = Object.fromEntries(
    (savePayload || []).map((c) => [c.serial, c])
  );
  const byModelTag = new Map();
  for (const card of savePayload || []) {
    // propaga o mesmo tag a todos os seriais do modelo via lookup abaixo
    byModelTag.set(card.serial, {
      name: card.nome,
      rarity: card.raridade,
      edition: card.edition,
      year: card.ano === "" || card.ano == null ? undefined : Number(card.ano) || card.ano,
      language: card.idioma,
      numbering: card.num,
    });
  }

  return (serials || []).map((s) => {
    const direct = bySerial[s?.serialNumber];
    if (direct) {
      return {
        ...s,
        invertida: direct.invertida === true,
        gradableModel: {
          ...s.gradableModel,
          tag: byModelTag.get(s.serialNumber),
        },
      };
    }
    // Mesmo modelo de um serial que salvamos: espelha o tag.
    const mid = String(modelIdOf(s) || "");
    const donor = (savePayload || []).find((c) => {
      const other = (serials || []).find((x) => x?.serialNumber === c.serial);
      return String(modelIdOf(other) || "") === mid && mid;
    });
    if (!donor) return s;
    return {
      ...s,
      gradableModel: {
        ...s.gradableModel,
        tag: byModelTag.get(donor.serial),
      },
    };
  });
};

/** Monta a carta da UI a partir do serial (prioriza tag gravado). */
const cardFromSerial = (s, gradePayload) => {
  const model = s?.gradableModel;
  const tag = model?.tag;
  const pick = (tagVal, modelVal) =>
    tagVal !== undefined && tagVal !== null ? tagVal : modelVal;

  const year = pick(tag?.year, model?.year);

  return {
    nome: pick(tag?.name, model?.name),
    raridade: pick(tag?.rarity, model?.rarity),
    edition: pick(tag?.edition, model?.edition),
    ano: year === undefined || year === null ? "" : String(year),
    idioma: pick(tag?.language, model?.language),
    num: pick(tag?.numbering, model?.numbering),
    ...gradePayload,
    serial: s?.serialNumber,
    invertida: s?.invertida === true,
    select: true,
  };
};

export {
  buildTagsSendData,
  buildPartitionErrorData,
  buildTagSavePayload,
  buildManualAuditPayload,
  applySavedTagsToSerials,
  cardFromSerial,
  baseTagFromModel,
  cardDiffersFromBase,
  modelIdOf,
  isManualCard,
};
